import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { useSystemDate } from './useSystemDate';

// ── Empty stats sentinel — returned when no company is active ──
const EMPTY_STATS = {
  totalSales: 0,
  totalPurchase: 0,
  cashBankBalance: 0,
  cashBalance: 0,
  bankBalance: 0,
  profitLoss: 0,
  receivables: 0,
  payables: 0,
  inventoryValue: 0,
  chartData: [] as any[],
  recentActivity: [] as any[],
  todayStats: { voucherCount: 0, totalAmount: 0, salesCount: 0, purchaseCount: 0, salesAmount: 0, purchaseAmount: 0 },
  topDebtors: [] as { name: string; amount: number }[],
  topCreditors: [] as { name: string; amount: number }[],
  healthCheck: { trialBalanceDiff: 0, isBalanced: true, overdueParties: 0, overdueAmount: 0, last7DaysVouchers: 0, totalVouchers: 0 },
};

export const useDashboardStats = () => {
  const activeCompany = useStore((state) => state.activeCompany);
  const systemDate = useSystemDate();

  return useLiveQuery(async () => {
    // ── Guard: no active company → return empty immediately (no DB queries) ──
    if (!activeCompany?.id) return EMPTY_STATS;

    // Snapshot companyId at query start.
    // Every subsequent DB call checks this to abort if company switched mid-flight.
    const companyId = activeCompany.id;

    const checkStillActive = async (): Promise<boolean> => {
      const current = await db.companies.get(companyId);
      return current?.isActive === 1;
    };

    // ── 1. Load vouchers (only for this company) ──
    const vouchers = await db.vouchers
      .where('companyId').equals(companyId)
      .filter(v => !v.deleted)
      .toArray();

    if (!(await checkStillActive())) return EMPTY_STATS;

    const voucherIds = vouchers.map(v => v.id!);
    const entries = voucherIds.length > 0
      ? await db.voucherEntries.where('voucherId').anyOf(voucherIds).toArray()
      : [];

    if (!(await checkStillActive())) return EMPTY_STATS;

    // ── 2. Sales & Purchase totals ──
    const salesVoucherIds = new Set(vouchers.filter(v => v.type === 'Sales').map(v => v.id!));
    const purchaseVoucherIds = new Set(vouchers.filter(v => v.type === 'Purchase').map(v => v.id!));

    const totalSales = entries
      .filter(e => salesVoucherIds.has(e.voucherId) && e.type === 'Cr')
      .reduce((sum, e) => sum + e.amount, 0);

    const totalPurchase = entries
      .filter(e => purchaseVoucherIds.has(e.voucherId) && e.type === 'Dr')
      .reduce((sum, e) => sum + e.amount, 0);

    // ── 3. Cash & Bank ──
    const [cashGroup, bankGroup] = await Promise.all([
      db.groups.where('companyId').equals(companyId).and(g => g.name === 'Cash-in-Hand').first(),
      db.groups.where('companyId').equals(companyId).and(g => g.name === 'Bank Accounts').first(),
    ]);

    if (!(await checkStillActive())) return EMPTY_STATS;

    const [cashLedgers, bankLedgers] = await Promise.all([
      db.ledgers.where('groupId').equals(cashGroup?.id || -1).toArray(),
      db.ledgers.where('groupId').equals(bankGroup?.id || -1).toArray(),
    ]);

    // Scope to this company only (groupId can collide across companies in IndexedDB)
    const safeCashLedgers = cashLedgers.filter(l => l.companyId === companyId);
    const safeBankLedgers = bankLedgers.filter(l => l.companyId === companyId);

    const allCashBankLedgerIds = [...safeCashLedgers.map(l => l.id!), ...safeBankLedgers.map(l => l.id!)];
    const cashBankEntries = allCashBankLedgerIds.length > 0
      ? await db.voucherEntries.where('ledgerId').anyOf(allCashBankLedgerIds).toArray()
      : [];

    if (!(await checkStillActive())) return EMPTY_STATS;

    let cashBalance = safeCashLedgers.reduce((sum, l) => sum + (l.balanceType === 'Dr' ? l.openingBalance : -l.openingBalance), 0);
    let bankBalance = safeBankLedgers.reduce((sum, l) => sum + (l.balanceType === 'Dr' ? l.openingBalance : -l.openingBalance), 0);

    const cashLedgerIdSet = new Set(safeCashLedgers.map(l => l.id!));
    cashBankEntries.forEach(e => {
      if (cashLedgerIdSet.has(e.ledgerId)) {
        cashBalance += e.type === 'Dr' ? e.amount : -e.amount;
      } else {
        bankBalance += e.type === 'Dr' ? e.amount : -e.amount;
      }
    });

    const cashBankBalance = cashBalance + bankBalance;

    // ── 4. P&L ──
    const [incomeGroups, expenseGroups] = await Promise.all([
      db.groups.where('companyId').equals(companyId).and(g => g.nature === 'Income').toArray(),
      db.groups.where('companyId').equals(companyId).and(g => g.nature === 'Expenses').toArray(),
    ]);

    if (!(await checkStillActive())) return EMPTY_STATS;

    const [incomeLedgers, expenseLedgers] = await Promise.all([
      incomeGroups.length > 0 ? db.ledgers.where('groupId').anyOf(incomeGroups.map(g => g.id!)).toArray() : Promise.resolve([]),
      expenseGroups.length > 0 ? db.ledgers.where('groupId').anyOf(expenseGroups.map(g => g.id!)).toArray() : Promise.resolve([]),
    ]);

    const safeIncomeLedgers = incomeLedgers.filter(l => l.companyId === companyId);
    const safeExpenseLedgers = expenseLedgers.filter(l => l.companyId === companyId);

    const [incomeEntries, expenseEntries] = await Promise.all([
      safeIncomeLedgers.length > 0 ? db.voucherEntries.where('ledgerId').anyOf(safeIncomeLedgers.map(l => l.id!)).toArray() : Promise.resolve([]),
      safeExpenseLedgers.length > 0 ? db.voucherEntries.where('ledgerId').anyOf(safeExpenseLedgers.map(l => l.id!)).toArray() : Promise.resolve([]),
    ]);

    if (!(await checkStillActive())) return EMPTY_STATS;

    const totalIncome = incomeEntries.reduce((sum, e) => sum + (e.type === 'Cr' ? e.amount : -e.amount), 0);
    const totalExpense = expenseEntries.reduce((sum, e) => sum + (e.type === 'Dr' ? e.amount : -e.amount), 0);
    const profitLoss = totalIncome - totalExpense;

    // ── 5. Receivables & Payables ──
    const [debtorsGroup, creditorsGroup] = await Promise.all([
      db.groups.where('companyId').equals(companyId).and(g => g.name === 'Sundry Debtors').first(),
      db.groups.where('companyId').equals(companyId).and(g => g.name === 'Sundry Creditors').first(),
    ]);

    const [debtorLedgers, creditorLedgers] = await Promise.all([
      db.ledgers.where('groupId').equals(debtorsGroup?.id || -1).toArray(),
      db.ledgers.where('groupId').equals(creditorsGroup?.id || -1).toArray(),
    ]);

    const safeDebtorLedgers = debtorLedgers.filter(l => l.companyId === companyId);
    const safeCreditorLedgers = creditorLedgers.filter(l => l.companyId === companyId);

    const [debtorEntries, creditorEntries] = await Promise.all([
      safeDebtorLedgers.length > 0 ? db.voucherEntries.where('ledgerId').anyOf(safeDebtorLedgers.map(l => l.id!)).toArray() : Promise.resolve([]),
      safeCreditorLedgers.length > 0 ? db.voucherEntries.where('ledgerId').anyOf(safeCreditorLedgers.map(l => l.id!)).toArray() : Promise.resolve([]),
    ]);

    if (!(await checkStillActive())) return EMPTY_STATS;

    const receivables =
      safeDebtorLedgers.reduce((sum, l) => sum + (l.balanceType === 'Dr' ? l.openingBalance : -l.openingBalance), 0) +
      debtorEntries.reduce((sum, e) => sum + (e.type === 'Dr' ? e.amount : -e.amount), 0);

    const payables =
      safeCreditorLedgers.reduce((sum, l) => sum + (l.balanceType === 'Cr' ? l.openingBalance : -l.openingBalance), 0) +
      creditorEntries.reduce((sum, e) => sum + (e.type === 'Cr' ? e.amount : -e.amount), 0);

    // ── 6. Inventory value ──
    const stockItems = await db.stockItems.where('companyId').equals(companyId).toArray();
    const inventoryValue = stockItems.reduce((sum, item) => sum + (item.openingValue || 0), 0);

    // ── 7. Chart Data (last 6 months) ──
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = new Date(systemDate).getMonth();
    const chartData = Array.from({ length: 6 }, (_, i) => {
      const m = (currentMonth - 5 + i + 12) % 12;
      return { name: months[m], sales: 0, purchase: 0 };
    });

    const chartMonthSet = new Set(chartData.map(d => d.name));
    vouchers.forEach(v => {
      const monthName = months[new Date(v.date).getMonth()];
      if (!chartMonthSet.has(monthName)) return;
      const dataPoint = chartData.find(d => d.name === monthName);
      if (!dataPoint) return;
      const vEntries = entries.filter(e => e.voucherId === v.id);
      const amount = vEntries.reduce((sum, e) => sum + e.amount, 0) / 2;
      if (v.type === 'Sales') dataPoint.sales += amount;
      if (v.type === 'Purchase') dataPoint.purchase += amount;
    });

    // ── 8. Recent Activity (top 10) ──
    const recentActivity = [...vouchers]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 10)
      .map(v => {
        const vEntries = entries.filter(e => e.voucherId === v.id);
        const amount = vEntries.reduce((sum, e) => sum + e.amount, 0) / 2;
        return { id: v.id, type: v.type, no: v.voucherNumber, date: v.date, amount };
      });

    // ── 9. Today's Stats ──
    const todayStr = new Date(systemDate).toISOString().split('T')[0];
    const todayVouchers = vouchers.filter(v => v.date === todayStr);
    const todayEntryAmounts = todayVouchers.map(v => {
      const vEntries = entries.filter(e => e.voucherId === v.id);
      return vEntries.reduce((sum, e) => sum + e.amount, 0) / 2;
    });
    const todaySalesVouchers = todayVouchers.filter(v => v.type === 'Sales');
    const todayPurchaseVouchers = todayVouchers.filter(v => v.type === 'Purchase');
    const todayStats = {
      voucherCount: todayVouchers.length,
      totalAmount: todayEntryAmounts.reduce((s, a) => s + a, 0),
      salesCount: todaySalesVouchers.length,
      purchaseCount: todayPurchaseVouchers.length,
      salesAmount: todaySalesVouchers.reduce((sum, v) => {
        const vE = entries.filter(e => e.voucherId === v.id);
        return sum + vE.reduce((s, e) => s + e.amount, 0) / 2;
      }, 0),
      purchaseAmount: todayPurchaseVouchers.reduce((sum, v) => {
        const vE = entries.filter(e => e.voucherId === v.id);
        return sum + vE.reduce((s, e) => s + e.amount, 0) / 2;
      }, 0),
    };

    // ── 10. Top 5 Debtors & Creditors ──
    const debtorBalances = safeDebtorLedgers.map(l => {
      const ledgerEntries = debtorEntries.filter(e => e.ledgerId === l.id);
      const bal = (l.balanceType === 'Dr' ? l.openingBalance : -l.openingBalance) +
        ledgerEntries.reduce((sum, e) => sum + (e.type === 'Dr' ? e.amount : -e.amount), 0);
      return { name: l.name, amount: bal };
    }).filter(d => d.amount > 0).sort((a, b) => b.amount - a.amount).slice(0, 5);

    const creditorBalances = safeCreditorLedgers.map(l => {
      const ledgerEntries = creditorEntries.filter(e => e.ledgerId === l.id);
      const bal = (l.balanceType === 'Cr' ? l.openingBalance : -l.openingBalance) +
        ledgerEntries.reduce((sum, e) => sum + (e.type === 'Cr' ? e.amount : -e.amount), 0);
      return { name: l.name, amount: bal };
    }).filter(c => c.amount > 0).sort((a, b) => b.amount - a.amount).slice(0, 5);

    // ── 11. Health Check ──
    let totalDrAll = 0, totalCrAll = 0;
    const allLedgers = await db.ledgers.where('companyId').equals(companyId).toArray();
    for (const l of allLedgers) {
      const ob = l.openingBalance || 0;
      if (l.balanceType === 'Dr') totalDrAll += ob; else totalCrAll += ob;
      const lEntries = entries.filter(e => e.ledgerId === l.id);
      lEntries.forEach(e => { if (e.type === 'Dr') totalDrAll += e.amount; else totalCrAll += e.amount; });
    }
    const trialBalanceDiff = Math.abs(totalDrAll - totalCrAll);

    const overdueParties = debtorBalances.filter(d => d.amount > 0).length;
    const overdueAmount = debtorBalances.reduce((s, d) => s + d.amount, 0);

    const sevenDaysAgo = new Date(systemDate);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const sevenDaysStr = sevenDaysAgo.toISOString().split('T')[0];
    const last7DaysVouchers = vouchers.filter(v => v.date >= sevenDaysStr).length;

    const healthCheck = {
      trialBalanceDiff,
      isBalanced: trialBalanceDiff < 0.01,
      overdueParties,
      overdueAmount,
      last7DaysVouchers,
      totalVouchers: vouchers.length,
    };

    return {
      totalSales,
      totalPurchase,
      cashBankBalance,
      cashBalance,
      bankBalance,
      profitLoss,
      receivables,
      payables,
      inventoryValue,
      chartData,
      recentActivity,
      todayStats,
      topDebtors: debtorBalances,
      topCreditors: creditorBalances,
      healthCheck,
    };
  // Depend on id only — avoids re-runs when unrelated company fields change
  }, [activeCompany?.id, systemDate]);
};
